import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { GetUniversalOrderSettingListParams, UniversalOrderSettingType } from "@/services/api/universalOrderSetting";
import { useGlobalStore } from "@/store/hooks";
import { useRequest } from "ahooks";
import { pickBy } from "lodash";
import { DependencyList } from "react";

interface UseOrderSettingProps<T> {
    type: UniversalOrderSettingType;
    defaultValue?: T;
    companyId?: number;
    shopId?: number;
    manual?: boolean;
    orderTab?: OrderTab;
}

export function useOrderSetting<T = unknown>(props: UseOrderSettingProps<T>, deps?: DependencyList) {
    const { type, companyId, shopId, defaultValue, orderTab, manual = false } = props;
    const globalStore = useGlobalStore();
    const orderSettingStore = globalStore.orderSettingStore;

    const { data: compositeData, loading, runAsync, mutate } = useRequest(
        async () => {
            const params = (pickBy(
                { companyId, type, shopId, orderTab },
                (value) => value !== undefined && value !== null,
            ) as unknown) as GetUniversalOrderSettingListParams;

            await orderSettingStore.fetchSettings(params);

            const setting = orderSettingStore.scopeSettingsMap.get(
                orderSettingStore.buildScopeKey({ companyId, type, orderTab, shopId }),
            );

            const settingContent = setting?.settingContent;

            if (!settingContent) {
                return {
                    settingContent: null,
                    parsedData: defaultValue,
                };
            }

            try {
                const parsedData = JSON.parse(settingContent) as T;
                return {
                    settingContent,
                    parsedData,
                };
            } catch (error) {
                console.error("parse order setting error:", error?.message);
                return {
                    settingContent,
                    parsedData: defaultValue,
                };
            }
        },
        {
            manual,
            refreshDeps: deps,
        },
    );

    // 提取解析后的数据
    const data = compositeData?.parsedData ?? defaultValue;
    const settingContent = compositeData?.settingContent ?? undefined;

    async function save(newData: T, hideModal = false) {
        const stringifiedData = JSON.stringify(newData);

        await orderSettingStore.saveSetting(
            {
                type,
                orderTab,
                shopId,
                settingContent: stringifiedData,
            },
            hideModal,
        );

        mutate({
            settingContent: stringifiedData,
            parsedData: newData,
        });
    }

    return {
        data,
        loading,
        save,
        runAsync,
        settingContent,
        mutate: (newData: T) =>
            mutate({
                settingContent: JSON.stringify(newData),
                parsedData: newData,
            }),
    };
}
